import { useEffect, useState } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { fetchWithAuth } from "@/lib/api/client"

interface TasaExitoData {
  mes: string
  tasa_exito: number
  total_licitaciones: number
  licitaciones_ganadas: number
}

export function ChartTasaExito() {
  const [data, setData] = useState<TasaExitoData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  )

  // Generar array de años disponibles (últimos 5 años + año actual)
  const currentYear = new Date().getFullYear()
  const availableYears = Array.from({ length: 5 }, (_, i) =>
    (currentYear - i).toString()
  )

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        // El backend acepta 'months' (lookback). Calcular meses desde el año seleccionado.
        const now = new Date()
        const currentYear = now.getFullYear()
        const yearDiff = currentYear - parseInt(selectedYear)
        const months = Math.max(12, yearDiff * 12 + now.getMonth() + 1)
        const result = await fetchWithAuth(
          `/api/v1/analytics/tasa-exito?months=${months}`
        )
        setData(result)
      } catch (error) {
        console.error("Error fetching tasa de éxito:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [selectedYear])

  if (loading) {
    return <ChartTasaExitoLoading />
  }

  // Calcular tasa de éxito promedio del año
  const tasaPromedioAnual =
    data.length > 0
      ? (
          data.reduce((sum, item) => sum + item.tasa_exito, 0) / data.length
        ).toFixed(1)
      : "0"

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
        <div>
          <CardTitle>Tasa de Éxito</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Promedio anual: <span className="font-semibold text-green-600">{tasaPromedioAnual}%</span>
          </p>
        </div>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="mes"
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius)",
              }}
              labelStyle={{
                color: "hsl(var(--foreground))",
                fontWeight: 600,
                marginBottom: "8px",
              }}
              itemStyle={{
                color: "hsl(var(--foreground))",
              }}
              formatter={(value, name) => {
                if (name === "tasa_exito") {
                  return [`${Number(value ?? 0).toFixed(1)}%`, "Tasa de Éxito"]
                }
                if (name === "licitaciones_ganadas") {
                  return [value, "Ganadas"]
                }
                if (name === "total_licitaciones") {
                  return [value, "Total"]
                }
                return [value, name]
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />
            <Line
              type="monotone"
              dataKey="tasa_exito"
              stroke="hsl(142 76% 36%)"
              strokeWidth={3}
              name="Tasa de Éxito (%)"
              dot={{ fill: "hsl(142 76% 36%)", r: 5 }}
              activeDot={{ r: 7 }}
            />
            <Line
              type="monotone"
              dataKey="licitaciones_ganadas"
              stroke="hsl(210 100% 60%)"
              strokeWidth={2}
              name="Licitaciones Ganadas"
              dot={{ fill: "hsl(210 100% 60%)", r: 4 }}
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function ChartTasaExitoLoading() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-10 w-[120px]" />
        </div>
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[350px] w-full" />
      </CardContent>
    </Card>
  )
}
